import { LogOut, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/use-auth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
  Badge,
} from '@/components/ui';

/**
 * User menu dropdown with profile info and logout.
 */
export function UserMenu() {
  const { user, signOut, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading || !user) {
    return null;
  }

  const roleKeys: Record<string, string> = {
    platform_admin: 'common:role.platformAdmin',
    platform_it: 'common:role.platformIt',
    platform_ops: 'common:role.platformOps',
    merchant_product_manager: 'common:role.merchantProductManager',
    merchant_it: 'common:role.merchantIt',
    merchant_viewer: 'common:role.viewer',
  };
  const roleLabel = t(roleKeys[user.role] ?? 'common:role.viewer');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full bg-primary text-primary-foreground coarse:min-h-11 coarse:min-w-11" aria-label={t('aria.openUserMenu')}>
          <User className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{user.email}</p>
            <div className="flex items-center gap-2 pt-1">
              <Badge variant="secondary" className="text-xs">
                {roleLabel}
              </Badge>
              {user.merchantId && (
                <span className="text-xs text-muted-foreground">ID: {user.merchantId}</span>
              )}
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => signOut()}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t('button.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
